import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback
} from 'react';
import { View } from '@adobe/react-spectrum';
import { useDispatch } from 'react-redux';
import { useHomeSlice } from 'app/pages/Home/slice';
import { parseDate, CalendarDate } from '@internationalized/date';
import { CalendarRange } from '../Fields/Calendar';
import { resolveCalendarRangeDefaults } from '../Fields/Calendar/calendarRangeDefaults';
import Edit from '@spectrum-icons/workflow/Edit';
import {
  formatToMonthDayYear,
  formatQuarter,
  formatMonth
} from '../../../utils/time';
import styles from './TimelineCard.module.scss';
import {
  MOMENT_TYPES,
  isEolMoment,
  isPriceChangeStyleOfferMoment
} from 'utils/constants';
import {
  pickCustomerNotificationDateComponentFields,
  pickEffectiveDateComponentFields,
  isSpecialDateStatus
} from 'utils/timelineDisplayDates';

/** The Moment `dates` shape this component reads. */
interface DateComponentRawDates {
  targetStart?: string;
  targetMonth?: string;
  targetQuarter?: string;
  effectiveDate?: string;
  effectiveMonth?: string;
  effectiveQuarter?: string;
  releaseDate?: string;
  releaseMonth?: string;
  releaseQuarter?: string;
  customerNotificationDate?: string;
  customerNotificationMonth?: string; // API field name
  customerNotificationDateMonth?: string; // legacy alias
  customerNotificationQuarter?: string; // API field name
  customerNotificationDateQuarter?: string; // legacy alias
  customerNotificationDateStatus?: string;
  effectiveDateStatus?: string;
}

/** Stable fallback so hook dependencies don't change every render. */
const NO_DATES: DateComponentRawDates = {};

interface DateComponentProps {
  /** Optional: the API can omit a Moment's `dates` object. */
  rawDates?: DateComponentRawDates;
  isDateClicked: boolean;
  dateType: 'release' | 'effective';
  className?: string;
  projectType?: string;
  /** New PCO moments: same `projectType` as legacy offers but `pco: true` on the card */
  pco?: boolean;
  isOverdue?: boolean; // ✅ NEW: Flag to indicate if dates are overdue
  isCalendarOpen?: boolean; // ✅ Controlled state from parent
  onCalendarToggle?: () => void; // ✅ Callback to toggle calendar
}

export const DateComponent: React.FC<DateComponentProps> = ({
  // A Moment with no `dates` (undefined/null) would otherwise throw and blank the page.
  rawDates: rawDatesProp,
  isDateClicked,
  dateType,
  className = '',
  projectType,
  pco = false,
  isOverdue = false, // ✅ NEW: Default to false if not provided
  isCalendarOpen: externalCalendarOpen, // ✅ Controlled state from parent
  onCalendarToggle // ✅ Callback to toggle calendar
}) => {
  const rawDates = rawDatesProp || NO_DATES;
  const dispatch = useDispatch();
  const { actions } = useHomeSlice();
  // Get the appropriate date fields based on dateType
  const getDateFields = useCallback(() => {
    /** Pass-through only for YYYY-MM-DD ISO strings; coarse "Q2 2027" / "Apr 2026" values
     *  stored in a "date" field by the backend must not reach formatToMonthDayYear. */
    const isoOnly = (v?: string) =>
      v && /^\d{4}-\d{2}-\d{2}/.test(v) ? v : undefined;

    if (isPriceChangeStyleOfferMoment(projectType, pco)) {
      if (dateType === 'release') {
        if (isSpecialDateStatus(rawDates.customerNotificationDateStatus)) {
          return {
            targetStart: undefined,
            targetMonth: undefined,
            targetQuarter: undefined
          };
        }
        // "release" slot = Customer Notification Date, falling back to targetStart
        // when the API has not yet populated customerNotificationDate.
        // Phased pco:true returns canonical `customerNotificationMonth` / `customerNotificationQuarter`
        // (no "Date" infix); legacy aliases with the "Date" infix are kept for backward compat.
        const custNotifMonth =
          rawDates.customerNotificationMonth ||
          rawDates.customerNotificationDateMonth;
        const custNotifQuarter =
          rawDates.customerNotificationQuarter ||
          rawDates.customerNotificationDateQuarter;
        return {
          targetStart:
            isoOnly(rawDates.customerNotificationDate) ||
            isoOnly(rawDates.targetStart),
          targetMonth: custNotifMonth || rawDates.targetMonth,
          targetQuarter: custNotifQuarter || rawDates.targetQuarter
        };
      }
      // "effective" slot = Effective Date
      return {
        targetStart: isoOnly(rawDates.effectiveDate),
        targetMonth: rawDates.effectiveMonth,
        targetQuarter: rawDates.effectiveQuarter
      };
    }

    if (isEolMoment(projectType)) {
      if (dateType === 'release') {
        if (isSpecialDateStatus(rawDates.customerNotificationDateStatus)) {
          return {
            targetStart: undefined,
            targetMonth: undefined,
            targetQuarter: undefined
          };
        }
        return pickCustomerNotificationDateComponentFields(rawDates);
      }
      return pickEffectiveDateComponentFields(rawDates);
    }

    if (
      projectType === MOMENT_TYPES.PROMOS_OFFERS ||
      projectType === MOMENT_TYPES.MARKETING_MOMENT
    ) {
      if (dateType === 'effective') {
        return {
          targetStart: isoOnly(rawDates.effectiveDate),
          targetMonth: rawDates.effectiveMonth,
          targetQuarter: rawDates.effectiveQuarter
        };
      }
      // Phased "Offer, Price Change, or Program" cards carry customerNotificationDate
      // instead of targetStart — treat them as equivalent (same two-date pattern).
      // API returns 'customerNotificationMonth'/'customerNotificationQuarter' (no "Date" infix).
      const custNotifMonth =
        rawDates.customerNotificationMonth ||
        rawDates.customerNotificationDateMonth;
      const custNotifQuarter =
        rawDates.customerNotificationQuarter ||
        rawDates.customerNotificationDateQuarter;
      return {
        targetStart:
          isoOnly(rawDates.targetStart) ||
          isoOnly(rawDates.customerNotificationDate),
        targetMonth: rawDates.targetMonth || custNotifMonth,
        targetQuarter: rawDates.targetQuarter || custNotifQuarter
      };
    } else {
      if (dateType === 'release') {
        return {
          targetStart: isoOnly(rawDates.releaseDate),
          targetMonth: rawDates.releaseMonth,
          targetQuarter: rawDates.releaseQuarter
        };
      }
      return {
        targetStart: isoOnly(rawDates.targetStart),
        targetMonth: rawDates.targetMonth,
        targetQuarter: rawDates.targetQuarter
      };
    }
  }, [dateType, rawDates, projectType, pco]);

  const dateFields = getDateFields();
  // Format the raw dates
  const releaseDate = formatToMonthDayYear(dateFields.targetStart);
  const releaseMonth = formatMonth(dateFields.targetMonth);
  const releaseQuarter = formatQuarter(dateFields.targetQuarter);

  // ✅ Use controlled state if provided, otherwise use internal state
  const [internalCalendarOpen, setInternalCalendarOpen] = useState(false);
  const isCalendarOpen =
    externalCalendarOpen !== undefined
      ? externalCalendarOpen
      : internalCalendarOpen;

  const [currentDate, setCurrentDate] = useState<CalendarDate | null>(null);
  const [currentSpecialValue, setCurrentSpecialValue] = useState<
    'TBD' | 'N/A' | null
  >(null);
  const [currentDateValue, setCurrentDateValue] = useState<string>('');
  const componentRef = useRef<HTMLDivElement>(null);
  const previousPropsRef = useRef<string>('');

  // Utility function to convert DD/MM/YYYY to YYYY-MM-DD
  const convertToYYYYMMDD = useCallback((dateString: string): string | null => {
    try {
      const [day, month, year] = dateString.split('/').map(Number);
      if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
      return `${year}-${month.toString().padStart(2, '0')}-${day
        .toString()
        .padStart(2, '0')}`;
    } catch (error) {
      return null;
    }
  }, []);

  // Format display value using the formatting functions
  const formatDisplayValue = useCallback(
    (value: string) => {
      if (!value) return '';

      // Handle quarter format (Q1 2025) - use formatQuarter
      if (value.startsWith('Q') && value.includes(' ')) {
        return formatQuarter(value);
      }

      // Handle month format (January 2025) - use formatMonth
      if (value.match(/^[A-Za-z]+ \d{4}$/)) {
        return formatMonth(value);
      }

      // Handle YYYY-MM-DD format - use formatToMonthDayYear
      if (value.includes('-')) {
        return formatToMonthDayYear(value);
      }

      // Handle DD/MM/YYYY format - convert to YYYY-MM-DD then format
      if (value.includes('/')) {
        const convertedDate = convertToYYYYMMDD(value);
        return convertedDate ? formatToMonthDayYear(convertedDate) : value;
      }

      // Handle special values (TBD, N/A)
      if (value === 'TBD' || value === 'N/A') {
        return value;
      }

      return value; // Return original if no pattern matches
    },
    [convertToYYYYMMDD]
  );

  const customerNotificationStatus =
    dateType === 'release' &&
    isSpecialDateStatus(rawDates.customerNotificationDateStatus)
      ? rawDates.customerNotificationDateStatus
      : undefined;

  const effectiveStatus =
    dateType === 'effective' &&
    isSpecialDateStatus(rawDates.effectiveDateStatus)
      ? rawDates.effectiveDateStatus
      : undefined;

  const specialDateStatus = customerNotificationStatus || effectiveStatus;

  // Determine the display value
  const initialDisplayValue = useMemo(
    () => specialDateStatus || releaseDate || releaseMonth || releaseQuarter,
    [specialDateStatus, releaseDate, releaseMonth, releaseQuarter]
  );

  const rawDisplayValue = useMemo(
    () => currentDateValue || initialDisplayValue,
    [currentDateValue, initialDisplayValue]
  );

  const releaseDateDisplay = useMemo(() => {
    const result = formatDisplayValue(rawDisplayValue);
    return result;
  }, [
    formatDisplayValue,
    rawDisplayValue,
    isDateClicked,
    currentDateValue,
    initialDisplayValue
  ]);

  const hasReleaseDate = useMemo(
    () =>
      !!(specialDateStatus || releaseDate || releaseMonth || releaseQuarter),
    [specialDateStatus, releaseDate, releaseMonth, releaseQuarter]
  );

  const calendarDefaults = useMemo(
    () =>
      resolveCalendarRangeDefaults(
        {
          isoDay: dateFields.targetStart,
          month: dateFields.targetMonth,
          quarter: dateFields.targetQuarter
        },
        currentDateValue
      ),
    [dateFields, currentDateValue]
  );

  // Initialize and sync currentDateValue with prop changes
  useEffect(() => {
    // Use the same formatting logic as handleCalendarDateChange
    let formattedValue = '';

    if (dateFields.targetQuarter) {
      // Format quarter like selection: "Q1 2024"
      formattedValue = dateFields.targetQuarter;
    } else if (dateFields.targetMonth) {
      // Format month like selection: "January 2024"
      formattedValue = dateFields.targetMonth;
    } else if (dateFields.targetStart) {
      // Format date like selection: "YYYY-MM-DD"
      if (dateFields.targetStart.includes('-')) {
        formattedValue = dateFields.targetStart;
      } else if (dateFields.targetStart.includes('/')) {
        // Use utility function to convert DD/MM/YYYY to YYYY-MM-DD
        const convertedDate = convertToYYYYMMDD(dateFields.targetStart);
        formattedValue = convertedDate || dateFields.targetStart;
      }
    }

    // Only update if the PROP value has changed (not user selection)
    // Compare with the previous prop value stored in ref
    if (formattedValue && formattedValue !== previousPropsRef.current) {
      previousPropsRef.current = formattedValue;
      setCurrentDateValue(formattedValue);
    } else if (!previousPropsRef.current && formattedValue) {
      // Initial mount: set both ref and state
      previousPropsRef.current = formattedValue;
      setCurrentDateValue(formattedValue);
    }
  }, [
    dateFields.targetStart,
    dateFields.targetMonth,
    dateFields.targetQuarter,
    convertToYYYYMMDD,
    isDateClicked
  ]);

  // Utility function to parse any date value for calendar
  const parseDateForCalendar = useCallback(
    (value: string): CalendarDate | undefined => {
      if (!value) return undefined;

      try {
        // Handle quarter format (Q1 2025) - return undefined for calendar
        if (value.startsWith('Q') && value.includes(' ')) {
          return undefined;
        }

        // Handle month format (January 2025) - return undefined for calendar
        if (value.match(/^[A-Za-z]+ \d{4}$/)) {
          return undefined;
        }

        // Handle YYYY-MM-DD format
        if (value.includes('-')) {
          const [year, month, day] = value.split('-').map(Number);
          if (isNaN(day) || isNaN(month) || isNaN(year)) return undefined;
          return parseDate(value);
        }

        // Handle DD/MM/YYYY format (old format)
        if (value.includes('/')) {
          const convertedDate = convertToYYYYMMDD(value);
          return convertedDate ? parseDate(convertedDate) : undefined;
        }

        return undefined;
      } catch (error) {
        return undefined;
      }
    },
    [convertToYYYYMMDD]
  );

  // Parse current date for calendar (like DecisionScreen)
  const parseDisplayDate = useMemo(() => {
    return parseDateForCalendar(currentDateValue);
  }, [parseDateForCalendar, currentDateValue]);

  // Update currentDate when currentDateValue changes
  useEffect(() => {
    setCurrentDate(parseDisplayDate || null);
  }, [parseDisplayDate]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        componentRef.current &&
        !componentRef.current.contains(event.target as Node)
      ) {
        if (onCalendarToggle && isCalendarOpen) {
          onCalendarToggle(); // Close via parent if controlled
        } else {
          setInternalCalendarOpen(false); // Close internal state if not controlled
        }
      }
    };

    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isCalendarOpen, onCalendarToggle]);

  const handleReleaseDateClick = useCallback(() => {
    if (isDateClicked) {
      if (onCalendarToggle) {
        onCalendarToggle(); // Use parent callback if controlled
      } else {
        setInternalCalendarOpen(prev => !prev); // Toggle internal state if not controlled
      }
      // currentDate will be updated automatically via useEffect when currentDateValue changes
    }
  }, [isDateClicked, onCalendarToggle]);

  // Utility function to format date as YYYY-MM-DD
  const formatDateAsYYYYMMDD = useCallback((date: CalendarDate): string => {
    return `${date.year}-${date.month.toString().padStart(2, '0')}-${date.day
      .toString()
      .padStart(2, '0')}`;
  }, []);

  const updateReduxDates = useCallback(
    (dateVar: string, dateKey: string) => {
      if (dateType === 'release') {
        dispatch(
          actions.updateTooltipDetails({
            releaseDate: dateVar,
            releaseDateKey: dateKey
          })
        );
      } else {
        dispatch(
          actions.updateTooltipDetails({
            effectiveDate: dateVar,
            effectiveDateKey: dateKey
          })
        );
      }
    },
    [dateType, dispatch, actions]
  );

  const handleCalendarDateChange = useCallback(
    (
      date: CalendarDate,
      isQuarterSelection?: boolean,
      selectedQuarter?: { quarter: number; year: number },
      isMonthSelection?: boolean,
      selectedMonth?: { month: string; year: number },
      specialValue?: 'TBD' | 'N/A' | null
    ) => {
      setCurrentSpecialValue(specialValue || null);
      // Close calendar after selection - use parent callback if controlled
      if (onCalendarToggle && isCalendarOpen) {
        onCalendarToggle();
      } else {
        setInternalCalendarOpen(false);
      }

      let dateVar = '';
      let dateKey = '';
      // Handle different selection types and update currentDateValue (like DecisionScreen)
      if (isQuarterSelection && selectedQuarter) {
        dateVar = `Q${selectedQuarter.quarter} ${selectedQuarter.year}`;
        setCurrentDate(null); // Don't set a specific date for quarter selection
        setCurrentDateValue(dateVar);
        previousPropsRef.current = dateVar; // Update ref to prevent sync conflict
        dateKey = 'Quarter';
      } else if (isMonthSelection && selectedMonth) {
        dateVar = `${selectedMonth.month} ${selectedMonth.year}`;
        setCurrentDate(null); // Don't set a specific date for month selection
        setCurrentDateValue(dateVar);
        previousPropsRef.current = dateVar; // Update ref to prevent sync conflict
        dateKey = 'Month';
      } else if (date && date.day && date.month && date.year) {
        // Use utility function to format date as YYYY-MM-DD
        dateVar = formatDateAsYYYYMMDD(date);
        setCurrentDate(parseDate(dateVar));
        setCurrentDateValue(dateVar);
        previousPropsRef.current = dateVar; // Update ref to prevent sync conflict
        dateKey = 'Date';
      } else if (specialValue) {
        setCurrentDate(null); // Don't set a specific date for special values
        setCurrentDateValue(specialValue);
        previousPropsRef.current = specialValue; // Update ref to prevent sync conflict
      }
      updateReduxDates(dateVar, dateKey);
    },
    [formatDateAsYYYYMMDD, updateReduxDates, onCalendarToggle, isCalendarOpen]
  );

  // For PCO-style moments (legacy pco:false and phased pco:true) always render both slots
  // so the user has an entry point to set Customer Notification Date / Effective Date
  // even when the moment was created without them (e.g. only effectiveDate populated).
  // Without this, the tooltip omits the row entirely and the field becomes unreachable.
  // EOL follows PRWM / New Release / Product Release: hide the row when that date is absent.
  const isPcoStyle = isPriceChangeStyleOfferMoment(projectType, pco);
  if (!hasReleaseDate && !isPcoStyle) {
    return null;
  }

  // Get the appropriate heading based on dateType
  const getHeading = () => {
    if (isEolMoment(projectType)) {
      return dateType === 'release'
        ? 'CUSTOMER NOTIFICATION DATE:'
        : 'EFFECTIVE DATE:';
    }
    if (isPriceChangeStyleOfferMoment(projectType, pco)) {
      return dateType === 'release'
        ? 'CUSTOMER NOTIFICATION DATE:'
        : 'EFFECTIVE DATE:';
    }
    if (
      projectType === MOMENT_TYPES.PROMOS_OFFERS ||
      projectType === MOMENT_TYPES.MARKETING_MOMENT
    ) {
      if (dateType === 'effective') return 'EFFECTIVE DATE:';
      // Phased cards carry customerNotificationDate instead of targetStart
      // API returns 'customerNotificationMonth'/'customerNotificationQuarter' (no "Date" infix).
      const isPhasedCard =
        !rawDates.targetStart &&
        !rawDates.targetMonth &&
        !rawDates.targetQuarter &&
        !!(
          rawDates.customerNotificationDate ||
          rawDates.customerNotificationMonth ||
          rawDates.customerNotificationDateMonth ||
          rawDates.customerNotificationQuarter ||
          rawDates.customerNotificationDateQuarter
        );
      return isPhasedCard ? 'CUSTOMER NOTIFICATION DATE:' : 'MARKETING DATE:';
    }
    return dateType !== 'release' ? 'MARKETING DATE:' : 'RELEASE DATE:';
  };

  return (
    <div ref={componentRef} className={styles.releaseDateContainer}>
      <div className={styles.titleWrapper}>
        <div
          className={`${styles.tooltipEffectiveDate} ${className} ${
            isDateClicked ? styles.editableReleaseDate : ''
          }`}
          onClick={handleReleaseDateClick}
        >
          <span className={styles.effectiveDateHeading}>{getHeading()}</span>
          <span
            className={`${styles.effectiveDateText} ${
              !isDateClicked ? styles.readOnlyDate : styles.editableDate
            } ${isOverdue ? styles.effectiveDateTextOverdue : ''}`}
          >
            {' '}
            {releaseDateDisplay ||
              (isPcoStyle && isDateClicked ? 'Set date' : '')}
          </span>
        </div>
        {isDateClicked && (
          <button
            className={styles.editButton}
            onClick={handleReleaseDateClick}
            aria-label="Edit date"
            title="Edit date"
          >
            <Edit size="S" />
          </button>
        )}
      </div>

      {isCalendarOpen && isDateClicked && (
        <div className={styles.calendarDropdown}>
          <CalendarRange
            defaultDate={calendarDefaults.defaultDate}
            defaultQuarter={calendarDefaults.defaultQuarter}
            defaultMonth={calendarDefaults.defaultMonth}
            onCalendarDateChange={handleCalendarDateChange}
            mode="single"
            skipTriggerButton={true}
            hideYearList={true}
            showTBDNA={false}
            disablePastDate={false}
            data-testid={`${dateType}-date-calendar`}
          />
        </div>
      )}
    </div>
  );
};
